import axios from 'axios';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AiOutlineQuestionCircle } from 'react-icons/ai';
import { FiDownload } from 'react-icons/fi';
import {
  RootState,
  useGetEventResponsesTableQuery,
} from '../store';
import config from '../config/config';
import Button from '../components/atoms/Button';
import FilterTable, { BaseData } from '../components/Table/Table';
import EmptyTable from '../components/Table/EmptyTable';
import AdminSectionTitle from '../components/AdminSectionTitle';

interface ResponseTable extends BaseData {
  name: JSX.Element;
  last_name: JSX.Element;
  person_id: JSX.Element;
  [key: string]: JSX.Element | string; // Dynamic columns for each question
}

const PollsPage: React.FC = () => {
  const { token } = useSelector(
    (state: RootState) => state.auth,
  );
  const { event_id: eventId, business_id: businessId } = useParams();
  const event = useSelector((state: RootState) => state.events.selectedEvent);
  const { data: responses } = useGetEventResponsesTableQuery({ eventId: eventId || '', businessId: businessId || '' });
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadExport = () => {
    setIsDownloading(true);
    axios
      .get(
        `${config.baseURL}/v1/businesses/${businessId}/events/${eventId}/polls/responses`,
        {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `respuestas_${event?.name ?? ''}_${new Date().toISOString()}.xlsx`,
        );
        document.body.appendChild(link);
        link.click();
      })
      .catch((e) => console.error(e))
      .finally(() => setIsDownloading(false));
  };

  const questions = responses && responses.length > 0
    ? responses[0].question_responses.map((q) => ({
      id: q.question_id,
      text: q.question_text,
    }))
    : [];

  const rows: ResponseTable[] = responses?.map((response) => {
    const row: Record<string, JSX.Element | string> = {
      id: response.id ?? '',
      name: <span className='whitespace-nowrap'>{response.name}</span>,
      last_name: <span className='whitespace-nowrap'>{response.last_name}</span>,
      person_id: <span className='whitespace-nowrap'>{response.person_id}</span>,
    };

    response.question_responses.forEach((qr) => {
      row[`question_${qr.question_id}`] = <span className='whitespace-nowrap'>{qr.value}</span>;
    });

    return row as ResponseTable;
  }) || [];

  const headCells = [
    {
      id: 'name',
      label: 'Nombre',
    },
    {
      id: 'last_name',
      label: 'Apellido',
    },
    {
      id: 'person_id',
      label: 'DNI',
    },
    ...questions.map((question) => ({
      id: `question_${question.id}`,
      label: question.text,
    })),
  ];

  return (
    <div className='flex flex-col gap-4 my-6'>
      <AdminSectionTitle title='Encuestas' subtitle={`${event?.name} - Encuestas`} />
      <div className='glass-surface'>
        <h1 className='text-2xl font-medium mb-2'>Encuestas</h1>
        {rows.length > 0 ? (
          <FilterTable
            rows={rows}
            headCells={headCells}
            tableId='polls-responses-table'
            pagination={{
              enabled: true,
              pageSize: 10,
            }}
            customButtons={[<Button onClick={handleDownloadExport} loading={isDownloading} variant='secondary'>
              <div className='flex flex-row gap-2 items-center'>
                <FiDownload className='text-xl' />
                <p className='whitespace-nowrap hidden md:block'>Descargar respuestas</p>
              </div>
            </Button>]}
          />
        ) : (
          <EmptyTable
            title='No hay respuestas'
            info='No hay respuestas para mostrar'
            icon={AiOutlineQuestionCircle}
            iconColor='text-secondary-500'
          />
        )}
      </div>
    </div>
  );
};

export default PollsPage;
